import type {
  EventBasedMarketView,
  MarketId,
  MarketLevelMarketTemplateId,
  MarketView,
  Runner
} from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import SelectionList from '@swf/sports/components/MarketTemplate/components/SelectionList/SelectionList';
import useMarketDisplayLimit from '@swf/sports/components/MarketTemplate/hooks/useMarketDisplayLimit';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';

interface Props {
  firstMarket: EventBasedMarketView;
  marketIds: MarketId[];
  templateId: MarketLevelMarketTemplateId;
}

interface GetRunnersArgs {
  marketIds: MarketId[];
  markets: Partial<Record<string, EventBasedMarketView>>;
}

const getRunners = ({ markets, marketIds }: GetRunnersArgs): (Runner & { market: MarketView })[] =>
  Object.values(markets)
    .filter(notEmpty)
    .filter((market) => marketIds.includes(market.marketId))
    .map((market) =>
      market.runners.map((runner) => ({
        ...runner,
        market
      }))
    )
    .flat();

const CombinedList: React.FC<Props> = ({ firstMarket, marketIds, templateId }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const markets = useSelector(widgetSelect.markets);

  const runners = useMemo(() => getRunners({ markets, marketIds }), [markets, marketIds]);

  const [runnersToDisplay, handleShowMoreClick, limitRunners] = useMarketDisplayLimit(
    runners,
    firstMarket.marketId
  );

  return (
    <SelectionList market={firstMarket} data-testid="market-template-list">
      {runnersToDisplay.map((runner) => (
        <SelectionList.Item
          key={runner.selectionId}
          runner={runner}
          showPreviousPrice={templateId === 'ODDS_BOOST' ? true : undefined}
          market={runner.market}
          eventStartTime={runner.market.marketTime}
        />
      ))}
      {limitRunners && <SelectionList.ShowMoreButton onClick={handleShowMoreClick} />}
    </SelectionList>
  );
};

export default CombinedList;
